// interfaces/types
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// externals
import { nanoid } from 'nanoid';

// consts/enums
import { actions } from '@makemydeal/dr-offer-redux';
import { OFFER_SAVE_FAILURE, OFFER_SAVE_NOCHANGE_SUCCESS, OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import { CALC_ENDPOINTS } from '../constants';
import { CASH, FINANCE, LEASE, AccelerateVehicleCondition } from '@makemydeal/dr-platform-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';

// utils
import { checkAccessoriesCatalogExists } from '../actions/accessoriesActionCreators';
import { updateVppSurchargesAttributes } from '../actions/vehicleProtectionActionCreators';
import { requoteAfterGuardedVehicleSwitch } from '../actions/vehicleActionCreators';
import {
    applyVehicleSwitchRetention,
    buildManualOnlyIncentivesAction,
    clearAppointmentDataForVehicleSwitch,
    clearItemsForVehicleSwitch,
    countIncentivesRetainedOnSwitch,
    hasProgramSourcedIncentive
} from '../actions/changeVehicleRetentionActionCreators';
import * as rollbackSelectors from '../selectors/rollbackSelectors';
import { dealerSelectors, featureToggleSelectors, rollbackActionCreators } from '@makemydeal/dr-shared-store';
import {
    changeVehicleCreators,
    changeVehicleSelectors,
    getKeepAllRetentionDecisions,
    ADD_NEW_SCENARIO_WITH_VEHICLE,
    CHANGE_VEHICLE_CLICKED,
    REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR,
    VEHICLE_CHANGE_FINISH
} from '@makemydeal/dr-activities-change-vehicle';
import type { RetentionDecisions } from '@makemydeal/dr-activities-change-vehicle';
import { getVppSurchargesOverrides } from '../selectors/vehicleProtectionSelectors';
import { getCalcEngineTransitionState, isAutoSelectProgramChecked } from '../selectors/BaseDealSelectors';
import {
    getIsUserProgramQuotes,
    getOfferDealerDiscountsTotalAmount,
    getDownPayment,
    getBuyRate,
    getApr,
    getSelectedTermMonths,
    getCalculationEndpointByOfferType
} from '../selectors/offerRedux';
import * as retentionSelectors from '../selectors/retentionSelectors';
import {
    buildVehicleSwitchVehicles,
    logCashFallbackTriggered,
    logCategoryRetention,
    logUpqFallbackTriggered,
    logUpqNoRetry,
    logVehicleSwitchCompleted
} from './vehicleSwitchTelemetry';
import type {
    RetentionCategory,
    VehicleSwitchFailureContext,
    VehicleSwitchInfo,
    VehicleSwitchOutcome
} from './vehicleSwitchTelemetry';
import { getIsEcommerceDeal } from '../selectors/dealXgDetailsSelectors';
import { dealerOptOutBlocksPqExcursion, forceCalcEndpointToPq, restoreCalcEndpoint } from '../actions/programActionCreators';
import { CALC_ENDPOINT_OWNED_BY_DISPATCHER_META, fallBackToUserProgramQuotes } from '../actions/programOrchestrationActionCreators';
import {
    openSlideOut,
    SlideOutType,
    setCalcEngineTransitionState,
    setIsAutoSelectProgramChecked
} from '../actions/baseDealActionCreators';
import { CLOSE_SLIDEOUT } from '../actionTypes/baseDealActionTypes';
import {
    actions as offerReduxActions,
    actionCreators as externalOfferReduxActionCreators,
    selectors as offerReduxSelectors,
    utils as offerReduxUtils
} from '@makemydeal/dr-offer-redux';
import * as offerReduxActionCreators from '../actions/offerRedux';
import { updatedFeesOverride } from '../actions/offerActionCreators';
import * as deskingActionCreators from '../actions/deskingActionCreators';
import * as paymentInputActionCreators from '../actions/paymentInput';
import * as avoActionCreators from '../actions/avoActionCreators';
import * as manualFieldOverridesActionCreators from '../actions/manualFieldOverridesActionCreators';
import * as manualFieldOverridesSelectors from '../selectors/manualFieldOverridesSelectors';
import { cloneDeep } from 'lodash';
import * as dealerUtils from '../utils/dealerUtils';
import { scopedActions, scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

const RETENTION_CATEGORIES: RetentionCategory[] = ['incentives', 'vpp', 'accessories', 'taxes'];
const ADD_NEW_SCENARIO_SWITCH_META = { addNewScenario: true, skipAiRefresh: true };

// `action.meta` is read unguarded: the only call site only reaches this function inside `action.meta && ...`.
// `requestCalculationEndpoint` is stamped before paymentPayloadUtils' own mid-request downgrade to
// program-quotes (missing required UPQ fields), so a downgraded call still reads as UPQ here unless that
// flag is checked too.
const shouldRequoteAfterGuardedSwitch = (state: any, action: AnyFSA): boolean => {
    if (action.meta.requestCalculationEndpoint !== CALC_ENDPOINTS.userProgramQuotes) return false;
    if (action.meta.originalAction.meta.missingRequiredUPQFields) return false;
    if (!featureToggleSelectors.areFeatureTogglesAvailable(state)) return false;
    return featureToggleSelectors.isRequoteAfterGuardedVehicleSwitchEnabled(state);
};

const buildSwitchFailureContext = (
    state: any,
    errorCategory: string | undefined,
    isAddNewScenario: boolean
): VehicleSwitchFailureContext => ({
    errorCategory,
    isEcommerceDeal: getIsEcommerceDeal(state),
    isAddNewScenario
});

const includeStandardIncentivesOption = (state: any): { includeStandardIncentives?: true } =>
    offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state) ? { includeStandardIncentives: true } : {};

const buildSwitchRetryAction = (state: any, selectedVehicle: any) =>
    externalOfferReduxActionCreators.switchVehicle(selectedVehicle, true, {
        preserveManualIncentives: true,
        clearFeesOverride: true,
        preserveIncentiveSelection: true,
        ...includeStandardIncentivesOption(state)
    });

const isCashFallbackEnabled = (state: any): boolean =>
    featureToggleSelectors.areFeatureTogglesAvailable(state) && featureToggleSelectors.isCashFallbackOnVehicleSwitchEnabled(state);

const isCashFallbackEligible = (state: any, offerType: OfferType): boolean =>
    (offerType === FINANCE || offerType === LEASE) &&
    isCashFallbackEnabled(state) &&
    !offerReduxSelectors.isFlexBuyPayment(state) &&
    dealerSelectors.isCashOfferEnabled(state);

const isGridHeldAfterCashFallback = (state: any, offerType: OfferType): boolean =>
    isCashFallbackEnabled(state) &&
    offerReduxSelectors.isPaymentGridAwaitingVehicleSwitch(state, offerType) &&
    !changeVehicleSelectors.isChangeProcessing(state);

const enterCashForSwitchFallback = (store: DashStore, state: any, offerType: OfferType, preSwitchCalcEndpoint: any) => {
    store.dispatch(setCalcEngineTransitionState({ cashDeal: preSwitchCalcEndpoint }));
    const offerTypeAction = offerReduxActionCreators.updatedOfferType(CASH, { payment: false, suppressAnalytics: true });
    store.dispatch({ ...offerTypeAction, meta: { ...offerTypeAction.meta, [CALC_ENDPOINT_OWNED_BY_DISPATCHER_META]: true } });
    if (preSwitchCalcEndpoint && getCalculationEndpointByOfferType(state, offerType) !== preSwitchCalcEndpoint) {
        store.dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(preSwitchCalcEndpoint, offerType));
    }
    store.dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes));
};

// Total number of retention-eligible items per category, sourced from the same selectors the
// retention modal renders from.
const getRetentionTotals = (state: any): Record<RetentionCategory, number> => ({
    incentives: (retentionSelectors.getIncentivesForRetentionModal(state) ?? []).length,
    vpp: (retentionSelectors.getVppForRetentionModal(state) ?? []).length,
    accessories: (retentionSelectors.getAccessoriesForRetentionModal(state) ?? []).length,
    taxes: (retentionSelectors.getManuallyOverriddenTaxItems(state) ?? []).length
});

const getRetentionKept = (state: any, retainedItems?: RetentionDecisions): Record<RetentionCategory, number> => ({
    incentives: retainedItems ? countIncentivesRetainedOnSwitch(state, retainedItems) : 0,
    vpp: retainedItems?.vpp?.length ?? 0,
    accessories: retainedItems?.accessories?.length ?? 0,
    taxes: retainedItems?.taxes?.length ?? 0
});

// Captures the vehicle-switch telemetry at CHANGE_VEHICLE_CLICKED (before the switch mutates the
// offer): emits one CategoryRetention event per category and returns the vehicles + keepAll decision
// to stash on the SWITCH_VEHICLE action meta so the terminal payment handler can complete the funnel
// purely from the action meta.
const buildVehicleSwitchInfo = (state: any, selectedVehicle: any, retainedItems?: RetentionDecisions): VehicleSwitchInfo => {
    const totals = getRetentionTotals(state);
    const kept = getRetentionKept(state, retainedItems);
    RETENTION_CATEGORIES.forEach((category) => {
        logCategoryRetention(category, kept[category], totals[category]);
    });
    const totalKept = RETENTION_CATEGORIES.reduce((sum, category) => sum + kept[category], 0);
    const totalAvailable = RETENTION_CATEGORIES.reduce((sum, category) => sum + totals[category], 0);
    return {
        vehicles: buildVehicleSwitchVehicles(changeVehicleSelectors.getVehicle(state), selectedVehicle),
        keepAll: totalKept === totalAvailable,
        totalItemsAvailable: totalAvailable,
        totalItemsRetained: totalKept
    };
};

// SWITCH_VEHICLE swaps in the new vehicle regardless of makePaymentCall, so both the non-AVO switch-payment
// failure and the AVO terms-apply failure must undo it the same way.
const rollbackOrRestoreAfterSwitchFailure = (store: DashStore, state: any) => {
    if (scopedStateSelectors.getIsScopedStoreEnabled(state)) {
        store.dispatch(rollbackActionCreators.rollbackScopeStateForSwitchVehicle());
    } else {
        store.dispatch(rollbackActionCreators.rollbackAppStateForSwitchVehicle(rollbackSelectors.getRollbackState(state)));
    }
};

// The lease terms an AVO apply seeds onto a scope: the card's down payment and money factor, plus the residual
// pair. Returned as a list so the caller dispatches them directly (Select) or wraps each one in a scoped action
// (Add New Scenario) — both call sites otherwise had to keep an identical copy of this block in step. The guard
// lives here for the same reason: it can't drift between the two.
//
// All of these are state-only (payment:false / skipPaymentCall), so the terminal rec-terms apply stays the single
// recalc. Both residual overrides are required: a lease UPQ request needs base, program AND adjusted
// (hasRequiredFieldsForUPQ), the base override writes base + program together, and the adjusted override writes
// only adjusted — so sending adjusted alone fails the check and the deal is downgraded to program quotes, which
// can't price a lease with no captive program.
const buildAvoLeaseOverrides = (
    recommendedPayment: any,
    termMonths: number | undefined,
    offerType: string,
    // Add New Scenario seeds the card's price onto the new scope; Select gets it from the vehicle switch.
    offerPrice?: number
): AnyFSA[] => {
    if (offerType !== LEASE || recommendedPayment.moneyFactor == null || termMonths == null) return [];

    const overrides: AnyFSA[] = [offerReduxActionCreators.updateDownPaymentImmediate(recommendedPayment.downPayment, LEASE)];

    if (offerPrice !== undefined) {
        overrides.push(offerReduxActionCreators.updateOfferPriceImmediate(offerPrice));
    }

    overrides.push(
        offerReduxActionCreators.updateSellRateOverride(recommendedPayment.moneyFactor, termMonths, {
            skipPaymentCall: true,
            rateType: 'MoneyFactor'
        })
    );

    if (recommendedPayment.residualPercent != null) {
        const residualValues = { value: recommendedPayment.residualPercent, months: termMonths, unitType: 'percent' as const };
        overrides.push(offerReduxActionCreators.updateBaseResidualOverride(residualValues, false));
        overrides.push(offerReduxActionCreators.updateAdjustedResidualOverride(residualValues, false));
    }

    return overrides;
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);
    switch (action.type) {
        case actions.PAYMENT_RECEIVED: {
            const paymentOriginalType = action.meta?.originalAction?.type;
            const isAddNewScenarioSeed = action.meta?.originalAction?.meta?.skipAiRefresh === true;
            // Terminal payment = switch quote (non-AVO) or rec-terms apply (AVO). The apply action is also used by
            // Add-New-Scenario, so gate on isChangeProcessing AND on the Add path's `skipAiRefresh` marker being
            // absent — otherwise a concurrent Add's payment could hijack this Change branch (single global flag).
            const isAvoChangeTerminalPayment =
                paymentOriginalType === actions.APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS &&
                changeVehicleSelectors.isChangeProcessing(store.getState()) &&
                !isAddNewScenarioSeed;
            if (action.meta && (paymentOriginalType === actions.SWITCH_VEHICLE || isAvoChangeTerminalPayment)) {
                const state = store.getState();
                const vehicleSwitchInfo: VehicleSwitchInfo | undefined = action.meta.originalAction.meta?.vehicleSwitchInfo;
                if (vehicleSwitchInfo) {
                    // Only report 'upqFallback' when this payment was a genuine retry after a PQ
                    // failure — not when the deal was already on UPQ before the switch.
                    const isFallbackRetry = action.meta.originalAction.meta.isFallbackRetry === true;
                    const isCashFallback = action.meta.originalAction.meta.isCashFallback === true;
                    const outcome: VehicleSwitchOutcome = isCashFallback
                        ? 'cashFallback'
                        : isFallbackRetry
                        ? 'upqFallback'
                        : 'success';
                    logVehicleSwitchCompleted(
                        vehicleSwitchInfo,
                        outcome,
                        buildSwitchFailureContext(
                            state,
                            action.meta.originalAction.meta.switchFailureCategory,
                            action.meta.originalAction.meta.addNewScenario === true
                        ),
                        isCashFallback ? action.meta.originalAction.meta.upqFallbackTriggered === true : undefined
                    );
                }
                store.dispatch(externalOfferReduxActionCreators.clearOverridesFallbackSnapshot());
                const cashFallbackFromOfferType = action.meta.originalAction.meta?.cashFallbackFromOfferType;
                store.dispatch(
                    changeVehicleCreators.finishVehicleChange(cashFallbackFromOfferType ? { cashFallbackFromOfferType } : undefined)
                );
                if (action.meta.originalAction.meta?.addNewScenario === true) {
                    store.dispatch({ type: REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR });
                }
                restoreCalcEndpoint(store.dispatch, state);
                store.dispatch(
                    externalOfferReduxActionCreators.rebuildPaymentGridAfterVehicleSwitch(
                        offerReduxSelectors.getCurrentOfferType(store.getState())
                    )
                );
                if (paymentOriginalType === actions.SWITCH_VEHICLE) {
                    const requoteState = store.getState();
                    if (shouldRequoteAfterGuardedSwitch(requoteState, action)) {
                        store.dispatch(requoteAfterGuardedVehicleSwitch(offerReduxSelectors.getCurrentOfferType(requoteState)));
                    }
                }
            } else {
                const state = store.getState();
                const offerType = offerReduxSelectors.getCurrentOfferType(state);
                if (isGridHeldAfterCashFallback(state, offerType)) {
                    store.dispatch(externalOfferReduxActionCreators.rebuildPaymentGridAfterVehicleSwitch(offerType));
                }
            }

            break;
        }
        case actions.PAYMENT_FAILURE: {
            // AVO: rec-terms apply is the terminal quote; on failure, fail the change so it doesn't hang. Gate on
            // isChangeProcessing AND the Add path's `skipAiRefresh` marker being absent (shared action type).
            if (
                action.meta &&
                action.meta.originalAction.type === actions.APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS &&
                changeVehicleSelectors.isChangeProcessing(store.getState()) &&
                action.meta.originalAction.meta?.skipAiRefresh !== true
            ) {
                rollbackOrRestoreAfterSwitchFailure(store, store.getState());
                // Restore the three writes the AVO apply made to turn Auto Select off, so a failed apply leaves the
                // deal exactly as it was (a scoped rollback never touches the global flag or this branch's UPQ force).
                const originalMeta = action.meta.originalAction.meta;
                if (originalMeta?.priorAutoSelectProgramChecked) {
                    store.dispatch(setIsAutoSelectProgramChecked(true));
                }
                if (originalMeta?.priorAutoSelectOfferType) {
                    if (typeof originalMeta.priorAutoSelectChecked === 'boolean') {
                        store.dispatch(
                            manualFieldOverridesActionCreators.setAutoSelectChecked(
                                originalMeta.priorAutoSelectOfferType,
                                originalMeta.priorAutoSelectChecked
                            )
                        );
                    }
                    if (originalMeta.priorCalcEndpoint) {
                        store.dispatch(
                            offerReduxActionCreators.updateCurrentCalculationEndpoint(
                                originalMeta.priorCalcEndpoint,
                                originalMeta.priorAutoSelectOfferType,
                                { withPayment: false, skipGridRebuild: true }
                            )
                        );
                    }
                }
                store.dispatch(changeVehicleCreators.vehicleChangeFailure());
                break;
            }
            if (action.meta && action.meta.originalAction.type === actions.SWITCH_VEHICLE) {
                const state = store.getState();
                const currentOfferType = offerReduxSelectors.getCurrentOfferType(state);
                const isAddNewScenarioSwitch = action.meta.originalAction.meta?.addNewScenario === true;
                const selectedVehicle = isAddNewScenarioSwitch
                    ? action.meta.originalAction.payload
                    : changeVehicleSelectors.getSelectedVehicleWithCustomData(state);
                const originalMeta = action.meta.originalAction.meta;
                const vehicleSwitchInfo: VehicleSwitchInfo | undefined = originalMeta?.vehicleSwitchInfo;
                const preSwitchCalcEndpoint = originalMeta?.preSwitchCalcEndpoint;
                const isCashFallbackRetry = originalMeta?.isCashFallback === true;
                const errorCategory = action.meta.error?.errorCategory;

                // When the payment failed on PQ, switch the calc endpoint to UPQ, restore the snapshotted
                // overrides, and retry the switch instead of rolling back state. When already on UPQ, no
                // further retry is made.
                // The switch goes through the shared primitive (AD-7) so this file and
                // `programInvalidationMiddleware` no longer each carry their own copy of it - the drift R-1
                // names. Options are documented on the primitive; what is specific here is that this flow
                // owns the endpoint (it forced program-quotes via `forceCalcEndpointToPq`) and restores its
                // own snapshot below, and that the retry carries the payment.
                //
                // The guard passes `currentOfferType` so it reads the same offer type the primitive checks,
                // rather than agreeing with it by derivation. No `errorCategory` check is needed for F-29:
                // the condition keys on the failure action and the switch behind it, never the category, so
                // it already fires for every value the service returns.
                if (!isCashFallbackRetry && !getIsUserProgramQuotes(state, currentOfferType)) {
                    logUpqFallbackTriggered();
                    fallBackToUserProgramQuotes(store, currentOfferType, {
                        withPayment: false,
                        callerOwnsEndpoint: true
                    });
                    store.dispatch(externalOfferReduxActionCreators.restoreOverridesForFallback());
                    // Forward the captured vehicle-switch info onto the retry so the eventual terminal
                    // payment action can still read the original vehicles / keepAll decision from meta.
                    const retryAction = buildSwitchRetryAction(state, selectedVehicle);
                    store.dispatch({
                        ...retryAction,
                        meta: {
                            ...retryAction.meta,
                            ...(vehicleSwitchInfo && { vehicleSwitchInfo }),
                            ...(preSwitchCalcEndpoint && { preSwitchCalcEndpoint }),
                            ...(isAddNewScenarioSwitch && ADD_NEW_SCENARIO_SWITCH_META),
                            isFallbackRetry: true,
                            switchFailureCategory: errorCategory
                        }
                    });
                    break;
                }
                const failureContext = buildSwitchFailureContext(state, errorCategory, isAddNewScenarioSwitch);
                if (!isCashFallbackRetry) {
                    logUpqNoRetry(failureContext);
                }
                if (!isCashFallbackRetry && isCashFallbackEligible(state, currentOfferType)) {
                    logCashFallbackTriggered(failureContext);
                    enterCashForSwitchFallback(store, state, currentOfferType, preSwitchCalcEndpoint);
                    const retryAction = buildSwitchRetryAction(state, selectedVehicle);
                    store.dispatch({
                        ...retryAction,
                        meta: {
                            ...retryAction.meta,
                            ...(vehicleSwitchInfo && { vehicleSwitchInfo }),
                            ...(isAddNewScenarioSwitch && ADD_NEW_SCENARIO_SWITCH_META),
                            isCashFallback: true,
                            cashFallbackFromOfferType: currentOfferType,
                            upqFallbackTriggered: originalMeta?.isFallbackRetry === true,
                            priorCalcEngineTransitionState: getCalcEngineTransitionState(state),
                            switchFailureCategory: errorCategory
                        }
                    });
                    break;
                }
                if (vehicleSwitchInfo) {
                    logVehicleSwitchCompleted(vehicleSwitchInfo, 'noRetry', failureContext);
                }

                rollbackOrRestoreAfterSwitchFailure(store, state);
                if (isCashFallbackRetry) {
                    store.dispatch(setCalcEngineTransitionState(originalMeta.priorCalcEngineTransitionState ?? {}));
                }
                store.dispatch(changeVehicleCreators.vehicleChangeFailure());
            }
            break;
        }
        case OFFER_SAVE_NOCHANGE_SUCCESS:
        case OFFER_SAVE_SUCCESS:
        case VEHICLE_CHANGE_FINISH: {
            if (changeVehicleSelectors.isChangeProcessing(store.getState())) {
                const state = store.getState();
                const surchargesOverrides = getVppSurchargesOverrides(state);
                store.dispatch(updateVppSurchargesAttributes(surchargesOverrides));
                store.dispatch(checkAccessoriesCatalogExists());
                store.dispatch(
                    changeVehicleCreators.vehicleChangeSuccess(action.type === VEHICLE_CHANGE_FINISH ? action.payload : undefined)
                );
            }
            break;
        }
        case CLOSE_SLIDEOUT: {
            const state = store.getState();
            if (changeVehicleSelectors.shouldOpenIncentivesAfterChange(state)) {
                const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state as any);
                store.dispatch(changeVehicleCreators.setOpenIncentivesAfterChange(false));
                store.dispatch(openSlideOut(SlideOutType.Incentives, primaryScopeId) as any);
            }
            break;
        }
        case CHANGE_VEHICLE_CLICKED: {
            const state = store.getState();
            // Strip `recommendedPayment` off the vehicle (unexpected field → NO_VALID_QUOTES); re-applied as AVO terms below.
            const { selectedVehicle: selectedVehicleWithRecommendedPayment, carryOverDiscounts } = action.payload;
            const { recommendedPayment, ...selectedVehicle } = selectedVehicleWithRecommendedPayment ?? {};
            const isScoped = scopedStateSelectors.getIsScopedStoreEnabled(state);
            const isAddNewScenario = action.meta?.addNewScenario === true;
            const retainedItems = isAddNewScenario ? getKeepAllRetentionDecisions(state) : action.payload.retainedItems;

            const hasRetainedItems = Boolean(retainedItems);

            // Capture the vehicle-switch telemetry before any dispatch mutates the offer so the
            // terminal payment handler can complete the funnel purely from the action meta.
            const vehicleSwitchInfo = isAddNewScenario
                ? undefined
                : buildVehicleSwitchInfo(state, selectedVehicle, retainedItems as RetentionDecisions | undefined);

            const rollbackAction = isScoped
                ? rollbackActionCreators.prepareScopeStateCopy()
                : rollbackActionCreators.prepareStateCopy(state);
            store.dispatch(rollbackAction);

            if (hasRetainedItems) {
                store.dispatch(applyVehicleSwitchRetention(retainedItems as RetentionDecisions));
            } else {
                store.dispatch(clearItemsForVehicleSwitch());
            }

            store.dispatch(clearAppointmentDataForVehicleSwitch());

            // NOTE: update offer's credit tiers
            const dealer = cloneDeep(dealerSelectors.getDealerState(state));
            const creditTiers = dealerUtils.getCreditTiersByVehicleCondition(dealer, selectedVehicle.condition);

            if (creditTiers) {
                dealer.creditTiers = creditTiers;

                store.dispatch(offerReduxActionCreators.initOfferCreditTiers({ dealer }));
            }

            const switchOfferType = offerReduxSelectors.getCurrentOfferType(state);
            // Finance/Lease only — Cash/Balloon have no Auto Select checkbox (mirrors SEEDED_OFFER_TYPES).
            const isAspOfferType = switchOfferType === FINANCE || switchOfferType === LEASE;
            const isUsedLease = selectedVehicle.type === AccelerateVehicleCondition.USED && switchOfferType === LEASE;
            const isAvoApply = Boolean(recommendedPayment) && isAspOfferType;

            store.dispatch(externalOfferReduxActionCreators.holdPaymentGridForVehicleSwitch(switchOfferType));

            if (isAvoApply) {
                // skipGridRebuild both suppresses the grid rebuild and triggers the ASP-off column-source realign.
                store.dispatch(
                    offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, switchOfferType, {
                        skipGridRebuild: true
                    })
                );
            } else if (offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state)) {
                if (!dealerOptOutBlocksPqExcursion(state, switchOfferType)) {
                    forceCalcEndpointToPq(store.dispatch, state, switchOfferType);
                }
            } else if (isUsedLease) {
                store.dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, LEASE));
            }

            // Remove existing dealer discounts when not carrying over, before switching vehicle
            if (!carryOverDiscounts && getOfferDealerDiscountsTotalAmount(state) > 0) {
                const makePaymentCall = false;
                store.dispatch(externalOfferReduxActionCreators.applyDealerDiscounts([], makePaymentCall));
            }

            // Snapshot the current overrides before the switch wipes them so they can be restored
            // if the subsequent PQ payment fails and we fall back to UPQ. Capture-only: it does not
            // drop any overrides itself (the SWITCH_VEHICLE reset already handles that for MV).
            store.dispatch(externalOfferReduxActionCreators.captureOverridesFallbackSnapshot());
            // AVO: makePaymentCall=false so the switch can't auto-select a program that overrides the card's rate.
            const switchAction = externalOfferReduxActionCreators.switchVehicle(selectedVehicle, !recommendedPayment, {
                preserveManualIncentives: hasRetainedItems,
                // AVO carries the deal's dealer fees onto the switched scenario, so keep the fee override (government fees are stripped and recomputed below); non-AVO retained switches still clear it.
                clearFeesOverride: hasRetainedItems && !recommendedPayment,
                preserveIncentiveSelection: hasRetainedItems,
                ...includeStandardIncentivesOption(state)
            });
            // AVO: telemetry rides the terminal rec-terms apply below, not the non-quoting switch.
            const preSwitchCalcEndpoint = getCalculationEndpointByOfferType(state, switchOfferType);
            const switchMeta = isAddNewScenario
                ? { ...ADD_NEW_SCENARIO_SWITCH_META, preSwitchCalcEndpoint }
                : { vehicleSwitchInfo, preSwitchCalcEndpoint };
            store.dispatch(recommendedPayment ? switchAction : { ...switchAction, meta: { ...switchAction.meta, ...switchMeta } });

            if (recommendedPayment) {
                // The change replaces the current recommendations; mark them stale so the next Change-Vehicle
                // open refetches. makePaymentCall=false emits no SWITCH_VEHICLE PAYMENT_RECEIVED, so avoMiddleware
                // never sees this change — mirror what the Add-New-Scenario branch does below.
                store.dispatch(avoActionCreators.markAvoStale());
                // Capture the prior global flag, scoped checkbox value, and calc endpoint so PAYMENT_FAILURE can
                // restore all three (the global baseDeal flag is a global reducer a scoped rollback never touches).
                const priorAutoSelectProgramChecked = isAutoSelectProgramChecked(state);
                const priorAutoSelectChecked = manualFieldOverridesSelectors.getIsAutoSelectChecked(state, switchOfferType);
                const priorCalcEndpoint = getCalculationEndpointByOfferType(state, switchOfferType);
                if (isAspOfferType) {
                    store.dispatch(setIsAutoSelectProgramChecked(false));
                    if (featureToggleSelectors.isImprovedAutoSelectEnabled(state)) {
                        store.dispatch(manualFieldOverridesActionCreators.setAutoSelectChecked(switchOfferType, false));
                    }
                    store.dispatch(manualFieldOverridesActionCreators.clearManualFieldOverrides());
                    store.dispatch(offerReduxActionCreators.setProgramFallbackOverrides());
                }
                if (
                    offerReduxSelectors.hasManualTotalTax(state, switchOfferType) ||
                    offerReduxSelectors.hasManualTaxItems(state, false, switchOfferType)
                ) {
                    store.dispatch({
                        ...offerReduxActionCreators.resetTaxOverrides(switchOfferType),
                        meta: { middleware: { payment: false } }
                    });
                }
                const switchFeesOverride = offerReduxSelectors.getFeesOverride(state, switchOfferType) ?? [];
                if (switchFeesOverride.some((fee) => fee.category === FeesCategoriesType.GOVERNMENT)) {
                    const dealerFeesOverride = switchFeesOverride.filter((fee) => fee.category !== FeesCategoriesType.GOVERNMENT);
                    store.dispatch(updatedFeesOverride(dealerFeesOverride, false));
                }
                buildAvoLeaseOverrides(recommendedPayment, getSelectedTermMonths(state), switchOfferType).forEach((override) =>
                    store.dispatch(override)
                );
                const recommendedVehiclePayload = {
                    ...selectedVehicle,
                    sellingPrice: selectedVehicle.listedPrice,
                    // payment service validates `vehicle.odometer` (omitting → NO_VALID_QUOTES)
                    odometer: selectedVehicle.mileage
                };
                const applyRecommendedTermsAction = paymentInputActionCreators.applyRecommendedVehiclePaymentDetails({
                    downPayment: recommendedPayment.downPayment ?? getDownPayment(state) ?? 0,
                    sellingPrice: selectedVehicle.listedPrice ?? 0,
                    retailPrice: selectedVehicle.retailPrice ?? 0,
                    buyRate: recommendedPayment.buyRate ?? getBuyRate(state) ?? 0,
                    apr: recommendedPayment.apr ?? getApr(state) ?? 0,
                    sellRate: recommendedPayment.sellRate,
                    vehicle: recommendedVehiclePayload
                });
                store.dispatch({
                    ...applyRecommendedTermsAction,
                    meta: {
                        ...applyRecommendedTermsAction.meta,
                        priorAutoSelectProgramChecked,
                        // Stashed so PAYMENT_FAILURE restores the scoped checkbox value + endpoint too (Finance/Lease only).
                        ...(isAspOfferType && {
                            priorAutoSelectOfferType: switchOfferType,
                            priorAutoSelectChecked,
                            priorCalcEndpoint
                        }),
                        vehicleSwitchInfo
                    }
                });
            }

            break;
        }
        case ADD_NEW_SCENARIO_WITH_VEHICLE: {
            const state = store.getState();
            const { selectedVehicle } = action.payload;
            // Split off `recommendedPayment` (AVO quoted terms) so it doesn't persist on the
            // vehicle record; it seeds the payment below instead.
            const { recommendedPayment, ...vehicleData } = selectedVehicle ?? {};
            const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state as any);
            const currentScopeId = scopedStateSelectors.getCurrentScopeId(state as any);
            const newScopeId = nanoid();

            // Change-Vehicle AI on: a new scenario seeds from the scenario in view (its deal type); else clone primary.
            const seedFromCurrentScope = featureToggleSelectors.isAiVehicleRecommendationsChangeVehicleEnabled(state);
            const sourceScopeId = seedFromCurrentScope ? currentScopeId ?? primaryScopeId : primaryScopeId;
            store.dispatch(scopedActionsCreators.cloneScope(sourceScopeId, {}, newScopeId));

            // The clone carries the source scenario's transient Change-Vehicle state (its selected search vehicle
            // and results); reset it on the new scope so its Change Vehicle opens clean instead of pre-selected on
            // the source's pick.
            store.dispatch(scopedActions.createScopedAction(newScopeId, changeVehicleCreators.resetOnAddNewScenario()));

            // A brand-new scenario is not stale: clear the AI-refresh flag inherited from the clone.
            store.dispatch(scopedActions.createScopedAction(newScopeId, deskingActionCreators.hideAIRefreshButton()));

            store.dispatch(scopedActions.createScopedAction(newScopeId, avoActionCreators.markAvoStale()));

            if (!recommendedPayment && featureToggleSelectors.isNewScenarioVehicleSwitchEnabled(state)) {
                store.dispatch(scopedActions.createScopedAction(newScopeId, { type: REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR }));
                store.dispatch(
                    scopedActions.createScopedAction(newScopeId, {
                        ...changeVehicleCreators.changeVehicleClicked(vehicleData, false),
                        meta: ADD_NEW_SCENARIO_SWITCH_META
                    })
                );
                break;
            }

            const vehiclePayload = {
                ...vehicleData,
                sellingPrice: vehicleData.listedPrice,
                odometer: vehicleData.mileage
            };
            store.dispatch(
                scopedActions.createScopedAction(
                    newScopeId,
                    deskingActionCreators.updateVehicleWithSelectedRecommendation(vehiclePayload)
                )
            );

            if (recommendedPayment) {
                const scenarioOfferType = offerReduxSelectors.getCurrentOfferType(state);
                if (scenarioOfferType === FINANCE || scenarioOfferType === LEASE) {
                    store.dispatch(scopedActions.createScopedAction(newScopeId, setIsAutoSelectProgramChecked(false)));
                    if (featureToggleSelectors.isImprovedAutoSelectEnabled(state)) {
                        store.dispatch(
                            scopedActions.createScopedAction(
                                newScopeId,
                                manualFieldOverridesActionCreators.setAutoSelectChecked(scenarioOfferType, false)
                            )
                        );
                    }
                    store.dispatch(
                        scopedActions.createScopedAction(newScopeId, manualFieldOverridesActionCreators.clearManualFieldOverrides())
                    );
                    store.dispatch(
                        scopedActions.createScopedAction(newScopeId, offerReduxActionCreators.setProgramFallbackOverrides())
                    );
                    store.dispatch(
                        scopedActions.createScopedAction(
                            newScopeId,
                            offerReduxActionCreators.updateCurrentCalculationEndpoint(
                                CALC_ENDPOINTS.userProgramQuotes,
                                scenarioOfferType,
                                { skipGridRebuild: true }
                            )
                        )
                    );
                }
                if (
                    offerReduxSelectors.hasManualTotalTax(state, scenarioOfferType) ||
                    offerReduxSelectors.hasManualTaxItems(state, false, scenarioOfferType)
                ) {
                    store.dispatch(
                        scopedActions.createScopedAction(newScopeId, {
                            ...offerReduxActionCreators.resetTaxOverrides(scenarioOfferType),
                            meta: { middleware: { payment: false } }
                        })
                    );
                }

                const scenarioFeesOverride = offerReduxSelectors.getFeesOverride(state, scenarioOfferType) ?? [];
                if (scenarioFeesOverride.some((fee) => fee.category === FeesCategoriesType.GOVERNMENT)) {
                    const dealerFeesOverride = scenarioFeesOverride.filter((fee) => fee.category !== FeesCategoriesType.GOVERNMENT);
                    store.dispatch(scopedActions.createScopedAction(newScopeId, updatedFeesOverride(dealerFeesOverride, false)));
                }

                if (hasProgramSourcedIncentive(state)) {
                    store.dispatch(
                        scopedActions.createScopedAction(newScopeId, {
                            ...buildManualOnlyIncentivesAction(state),
                            meta: { middleware: { payment: false } }
                        })
                    );
                }

                buildAvoLeaseOverrides(
                    recommendedPayment,
                    getSelectedTermMonths(state),
                    scenarioOfferType,
                    vehicleData.listedPrice
                ).forEach((override) => store.dispatch(scopedActions.createScopedAction(newScopeId, override)));
            }

            // AVO-sourced → seed at the card's quoted terms. A standard-search scenario whose source is on
            // program quotes must price the new vehicle under that program, so seed price/down payment only and
            // let the program pick the rate rather than pinning the source scenario's. Gate on the per-scenario
            // calc endpoint (the global baseDeal auto-select flag is undefined on a freshly-loaded deal and
            // sticky across scenarios).
            const newScenarioOfferType = offerReduxSelectors.getCurrentOfferType(state);
            const priceUnderProgram =
                !recommendedPayment &&
                getCalculationEndpointByOfferType(state, newScenarioOfferType) === CALC_ENDPOINTS.programQuotes;
            const paymentPayload = {
                downPayment: recommendedPayment?.downPayment ?? getDownPayment(state) ?? 0,
                sellingPrice: vehicleData.listedPrice ?? 0,
                retailPrice: vehicleData.retailPrice ?? 0,
                buyRate: recommendedPayment?.buyRate ?? getBuyRate(state) ?? 0,
                apr: recommendedPayment?.apr ?? getApr(state) ?? 0,
                sellRate: recommendedPayment?.sellRate,
                vehicle: vehiclePayload
            };
            store.dispatch(
                scopedActions.createScopedAction(
                    newScopeId,
                    // skipAiRefresh: seeding a new scenario is not a user deal edit, so don't surface the
                    // AI "refresh" (stale) button on the freshly-created scope.
                    paymentInputActionCreators.applyRecommendedVehiclePaymentDetails(paymentPayload, {
                        skipAiRefresh: true,
                        ...(priceUnderProgram && { skipRateOverride: true })
                    })
                )
            );

            break;
        }
        case OFFER_SAVE_FAILURE: {
            store.dispatch(changeVehicleCreators.vehicleChangeFailure());
            break;
        }
        case offerReduxActions.SWITCH_VEHICLE: {
            const state = store.getState();

            const currentSurcharges = getVppSurchargesOverrides(state);
            const newVehicle = action.payload;

            const updatedSurcharges = {
                ...currentSurcharges,
                inServiceDate: newVehicle.inServiceDate
            };

            store.dispatch(updateVppSurchargesAttributes(updatedSurcharges));

            store.dispatch(changeVehicleCreators.processSwitchVehicle());
            break;
        }
        default:
            break;
    }
};
